import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  FormHelperText,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useContactForm, useFieldError } from '../../hooks/useContactForm';
import contactService from '../../services/ContactService';
import { ContactFormData } from '../../services/ApiService';
import ContactFormErrorHandler, { 
  ContactFormError, 
  parseApiError, 
  createContactFormError 
} from './ContactFormErrorHandler';

interface ContactFormErrors {
  name?: string;
  email?: string;
  company?: string;
  phone?: string;
  projectType?: string;
  budget?: string;
  message?: string;
}

interface ContactFormProps {
  initialData?: Partial<ContactFormData>;
}

const projectTypes = [
  'Web Application',
  'Mobile App',
  'E-commerce',
  'API Development',
  'Database Design',
  'DevOps & Infrastructure',
  'Other'
];

const budgetRanges = [
  'Under $5,000',
  '$5,000 - $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  '$50,000 - $100,000',
  'Over $100,000'
];

const ContactForm: React.FC<ContactFormProps> = ({ initialData = {} }) => {
  const theme = useTheme();
  
  // Form data state
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    budget: '',
    message: '',
    ...initialData,
  });

  // Use the custom hook for form submission
  const {
    isSubmitting,
    isSuccess,
    error,
    validationErrors,
    submitForm,
    resetForm,
    successMessage,
    requestId
  } = useContactForm();

  // Local state for field-specific errors and touched fields
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<ContactFormErrors>({});
  
  const [retryCount, setRetryCount] = useState(0);
  const [parsedError, setParsedError] = useState<ContactFormError | null>(null);

  // Transform API error to our error format
  React.useEffect(() => {
    if (error) {
      const errorObj = typeof error === 'string' 
        ? createContactFormError('unknown', error)
        : parseApiError(error);
      setParsedError(errorObj);
    } else {
      setParsedError(null);
    }
  }, [error]);

  // Handle retry with exponential backoff
  const handleRetry = async () => {
    setRetryCount(prev => prev + 1);
    
    // Add exponential backoff delay
    const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Retry the form submission
    if (formData) {
      await submitForm(formData);
    }
  };

  // Reset retry count on successful submission
  React.useEffect(() => {
    if (isSuccess) {
      setRetryCount(0);
      setParsedError(null);
    }
  }, [isSuccess]);

  const handleDismissError = () => {
    setParsedError(null);
    // Also clear the original error from the hook if resetForm supports it
    // resetForm({ clearError: true });
  };

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone.trim()) return true; // Phone is optional
    // Allow various phone formats
    const phoneRegex = /^[\+]?[(]?[\d\s\-\.\(\)]{10,}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateForm = (): boolean => {
    const newErrors: ContactFormErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Project description is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Project description must be at least 20 characters';
    }

    // Optional field validation
    if (formData.projectType && !projectTypes.includes(formData.projectType)) {
      newErrors.projectType = 'Please select a valid project type';
    }

    if (formData.budget && !budgetRanges.includes(formData.budget)) {
      newErrors.budget = 'Please select a valid budget range';
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ContactFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof ContactFormData) => (
    event: any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user makes selection
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Announce form submission start to screen readers
    const announceRegion = document.getElementById('form-announcements');
    if (announceRegion) {
      announceRegion.textContent = 'Submitting contact form, please wait...';
    }
    
    if (!validateForm()) {
      // Announce validation errors to screen readers
      if (announceRegion) {
        announceRegion.textContent = 'Please correct the errors in the form before submitting.';
      }
      
      // Focus on first error field for keyboard accessibility
      const firstErrorField = Object.keys(fieldErrors)[0];
      if (firstErrorField) {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
        if (errorElement) {
          errorElement.focus();
        }
      }
      return;
    }

    try {
      await submitForm(formData);
      
      // Announce success to screen readers
      if (announceRegion) {
        announceRegion.textContent = 'Contact form submitted successfully! We will get back to you soon.';
      }
      
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        projectType: '',
        budget: '',
        message: '',
      });
      setFieldErrors({});
      setTouched({});

      // Track form submission for analytics
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'form_submit', {
          form_name: 'contact_form',
          success: true,
        });
      }
    } catch (err) {
      // Announce error to screen readers
      if (announceRegion) {
        announceRegion.textContent = 'There was an error submitting the form. Please try again or contact us directly.';
      }
      
      // Error handling is done by the hook
      console.error('Form submission error:', err);
    }
  };

  // Track form start for analytics
  React.useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'form_start', {
        form_name: 'contact_form'
      });
    }
  }, []);

  if (isSuccess) {
    return (
      <Container maxWidth="md">
        <Card sx={{ mt: 4, textAlign: 'center', p: 4 }}>
          <CheckCircleIcon 
            sx={{ 
              fontSize: 64, 
              color: 'success.main', 
              mb: 2 
            }} 
          />
          <Typography
            variant="h4"
            component="h2"
            data-testid="success-message"
            sx={{ mb: 2, color: 'success.main', fontWeight: 600 }}
          >
            Thank you for your message!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {successMessage || 'We will get back to you soon. Our team typically responds within 4-6 hours during business days.'}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => resetForm()}
            sx={{ mt: 2 }}
          >
            Send Another Message
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          id="contact-form-title"
          data-testid="contact-form-title"
          sx={{
            textAlign: 'center',
            mb: 2,
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          Get In Touch
        </Typography>
        <Typography
          variant="h6"
          id="contact-form-description"
          data-testid="contact-form-subtitle"
          sx={{
            textAlign: 'center',
            mb: 6,
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Ready to start your project? Tell us about your requirements and we'll get back to you with a detailed proposal.
        </Typography>

        <Card sx={{ p: { xs: 3, md: 4 } }}>
          <CardContent>
            {/* ARIA live region for form announcements */}
            <div
              id="form-announcements"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            />
            
            <Box
              component="form"
              data-testid="contact-form"
              role="form"
              aria-labelledby="contact-form-title"
              aria-describedby="contact-form-description"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              {/* Enhanced Error Display */}
              <ContactFormErrorHandler
                error={parsedError}
                onRetry={handleRetry}
                onDismiss={handleDismissError}
                retryCount={retryCount}
                maxRetries={3}
              />

              {/* Show validation errors from API */}
              {validationErrors.length > 0 && (
                <Alert 
                  severity="warning" 
                  data-testid="validation-errors"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" component="div">
                    Please fix the following issues:
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {validationErrors.map((err, index) => (
                        <li key={index}>{err.field}: {err.message}</li>
                      ))}
                    </ul>
                  </Typography>
                </Alert>
              )}

              {/* Name Field */}
              <TextField
                label="Full Name"
                name="name"
                required
                fullWidth
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
                placeholder="Enter your full name"
                aria-label="Full Name"
                aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                FormHelperTextProps={{
                  id: 'name-error',
                  role: 'alert'
                }}
              />

              {/* Email Field */}
              <TextField
                label="Email Address"
                name="email"
                type="email"
                required
                fullWidth
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                placeholder="your.email@company.com"
                aria-label="Email Address"
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                FormHelperTextProps={{
                  id: 'email-error',
                  role: 'alert'
                }}
              />

              {/* Company Field */}
              <TextField
                label="Company Name"
                name="company"
                required
                fullWidth
                value={formData.company}
                onChange={handleInputChange('company')}
                error={!!fieldErrors.company}
                helperText={fieldErrors.company}
                placeholder="Your company name"
                aria-label="Company Name"
                aria-describedby={fieldErrors.company ? 'company-error' : undefined}
                FormHelperTextProps={{
                  id: 'company-error',
                  role: 'alert'
                }}
              />

              {/* Phone Field */}
              <TextField
                label="Phone Number"
                name="phone"
                fullWidth
                value={formData.phone}
                onChange={handleInputChange('phone')}
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone || 'Optional - for faster communication'}
                placeholder="+1 (555) 123-4567"
                aria-label="Phone Number"
                aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
                FormHelperTextProps={{
                  id: 'phone-error',
                  role: fieldErrors.phone ? 'alert' : undefined
                }}
              />

              {/* Project Type Field */}
              <FormControl fullWidth>
                <InputLabel id="project-type-label">Project Type</InputLabel>
                <Select
                  labelId="project-type-label"
                  label="Project Type"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleSelectChange('projectType')}
                  error={!!fieldErrors.projectType}
                  aria-label="Project Type"
                >
                  <MenuItem value="">
                    <em>Select project type</em>
                  </MenuItem>
                  {projectTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.projectType && (
                  <FormHelperText
                    error
                    data-testid="error-projectType"
                    role="alert"
                  >
                    {fieldErrors.projectType}
                  </FormHelperText>
                )}
              </FormControl>

              {/* Budget Field */}
              <FormControl fullWidth>
                <InputLabel id="budget-label">Budget Range</InputLabel>
                <Select
                  labelId="budget-label"
                  label="Budget Range"
                  name="budget"
                  value={formData.budget}
                  onChange={handleSelectChange('budget')}
                  error={!!fieldErrors.budget}
                  aria-label="Budget Range"
                >
                  <MenuItem value="">
                    <em>Select budget range</em>
                  </MenuItem>
                  {budgetRanges.map((range) => (
                    <MenuItem key={range} value={range}>
                      {range}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.budget && (
                  <FormHelperText
                    error
                    data-testid="error-budget"
                    role="alert"
                  >
                    {fieldErrors.budget}
                  </FormHelperText>
                )}
              </FormControl>

              {/* Message Field */}
              <TextField
                label="Project Description"
                name="message"
                required
                fullWidth
                multiline
                rows={4}
                value={formData.message}
                onChange={handleInputChange('message')}
                error={!!fieldErrors.message}
                helperText={fieldErrors.message || 'Tell us about your project requirements, goals, and timeline'}
                placeholder="Describe your project in detail..."
                aria-label="Project Description"
                aria-describedby={fieldErrors.message ? 'message-error' : undefined}
                FormHelperTextProps={{
                  id: 'message-error',
                  role: fieldErrors.message ? 'alert' : undefined
                }}
              />

              {/* Submit Button */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                {isSubmitting && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress size={24} data-testid="loading-indicator" />
                  </Box>
                )}
                
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  data-testid="submit-button"
                  aria-describedby="form-announcements"
                  aria-label={isSubmitting ? 'Submitting contact form, please wait' : 'Submit contact form'}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    minWidth: 200,
                    // Enhanced focus indication for keyboard users
                    '&:focus': {
                      outline: '3px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                  endIcon={isSubmitting ? null : <SendIcon />}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Or contact us directly
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="primary" />
              <Typography
                component="a"
                href="mailto:contact@vegadigitalsolutions.com"
                data-testid="contact-email"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                contact@vegadigitalsolutions.com
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ContactForm;
